import ClipperLib from 'clipper-lib';
import profiles from '../scene/fixed-window-profiles.json';

export const WINDOW_SHAPES = ['rectangle', 'arch', 'circle', 'oval', 'triangle', 'trapezoid'];
export const SHAPE_LABELS = { rectangle: 'Rectangle', arch: 'Arch', circle: 'Circle', oval: 'Oval', triangle: 'Triangle', trapezoid: 'Trapezoid' };
export const CLIP_SCALE = 1000000;
export const clipPath = points => points.map(([x, y]) => ({ X: Math.round(x * CLIP_SCALE), Y: Math.round(y * CLIP_SCALE) }));
export const fromClip = points => points.map(p => [p.X / CLIP_SCALE, p.Y / CLIP_SCALE]);
export const fixedGlassInset = id => Math.max(.035, profiles[id].height_mm / 1000 - .012);

export function windowOutline(shape, width, height) {
  if (shape === 'circle' || shape === 'oval') return Array.from({ length: 64 }, (_, i) => {
    const a = i * Math.PI / 32; return [width / 2 * Math.cos(a), height / 2 + height / 2 * Math.sin(a)];
  });
  if (shape === 'triangle') return [[-width / 2, 0], [width / 2, 0], [0, height]];
  if (shape === 'trapezoid') return [[-width / 2, 0], [width / 2, 0], [width * .3, height], [-width * .3, height]];
  if (shape === 'arch') {
    const rise = Math.min(width / 2, height / 2);
    return [[-width / 2, 0], [width / 2, 0], ...Array.from({ length: 33 }, (_, i) => [width / 2 * Math.cos(i * Math.PI / 32), height - rise + rise * Math.sin(i * Math.PI / 32)])];
  }
  return [[-width / 2, 0], [width / 2, 0], [width / 2, height], [-width / 2, height]];
}

export function insetOutline(points, distance) {
  const offset = new ClipperLib.ClipperOffset(10, .00001 * CLIP_SCALE), result = [];
  offset.AddPath(clipPath(points), ClipperLib.JoinType.jtMiter, ClipperLib.EndType.etClosedPolygon);
  offset.Execute(result, -distance * CLIP_SCALE);
  return result.length === 1 ? fromClip(result[0]) : [];
}

export function fixedShapeError(design) {
  const width = Number(design.run_a.width_in), height = Number(design.height_in), shape = design.window_shape || 'rectangle';
  const corner = design.layout === 'corner';
  if (corner && shape !== 'rectangle') return 'A 90° corner needs two rectangular panes.';
  if (corner && (!design.run_b || !Number.isFinite(Number(design.run_b.width_in)) || Number(design.run_b.width_in) < 12 || Number(design.run_b.width_in) > 240)) return 'Enter a Side B width from 12 to 240 inches.';
  if (Number(design.run_a.panels) !== 1 || (corner && Number(design.run_b.panels) !== 1)) return 'Fixed windows need one non-operable pane per side.';
  if (![width, height].every(v => Number.isFinite(v) && v >= 12 && v <= 240)) return 'Enter dimensions from 12 to 240 inches.';
  if (shape === 'circle' && Math.abs(width - height) > .0001) return 'A circle needs equal width and height.';
  const points = windowOutline(shape, width * .0254, height * .0254);
  const inner = insetOutline(points, profiles[design.model_id].height_mm / 1000 + .01);
  if (inner.length < 3 || Math.abs(ClipperLib.Clipper.Area(clipPath(inner))) / CLIP_SCALE ** 2 < .0004) return 'This shape is too narrow for the frame. Increase its width or height.';
  return '';
}

export function fixedGlassOutline(design, run = design.run_a) {
  const width = Number(run.width_in) * .0254, height = Number(design.height_in) * .0254, inset = fixedGlassInset(design.model_id);
  if (design.layout === 'corner') return [[-width / 2 + inset, inset], [width / 2 - .016, inset], [width / 2 - .016, height - inset], [-width / 2 + inset, height - inset]];
  return insetOutline(windowOutline(design.window_shape || 'rectangle', width, height), inset);
}

export function fixedGlassBounds(design, run = design.run_a) {
  if (![run.width_in, design.height_in].every(v => Number.isFinite(Number(v)) && Number(v) > 0)) return { width: 0, height: 0 };
  const points = fixedGlassOutline(design, run);
  if (points.length < 3) return { width: 0, height: 0 };
  const xs = points.map(p => p[0]), ys = points.map(p => p[1]);
  return { width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
}